'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from './cn';

type Tone = 'neutral' | 'error';
type ToastAction = { label: string; onClick: () => void };
type ToastItem = {
  id: number;
  message: ReactNode;
  tone: Tone;
  action?: ToastAction;
  duration: number;
};
type ShowOptions = { action?: ToastAction; tone?: Tone; duration?: number };

type ToastApi = {
  /** A result that is elsewhere or reversible: « Mail archivé », with « Annuler » for 5 s. */
  show: (message: ReactNode, options?: ShowOptions) => void;
  error: (message: ReactNode, options?: Omit<ShowOptions, 'tone'>) => void;
  /** Hides the notification shown, e.g. once its action was done another way (a shortcut). */
  dismiss: () => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() needs a <ToastProvider> above it');
  return api;
}

/** One notification at a time, bottom centre. It stays while hovered. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastItem | null>(null);
  const [leaving, setLeaving] = useState(false);
  const counter = useRef(0);

  const show = useCallback((message: ReactNode, options: ShowOptions = {}) => {
    counter.current += 1;
    setLeaving(false);
    setToast({
      id: counter.current,
      message,
      tone: options.tone ?? 'neutral',
      action: options.action,
      duration: options.duration ?? (options.tone === 'error' ? 7000 : 5000),
    });
  }, []);
  const error = useCallback(
    (message: ReactNode, options: Omit<ShowOptions, 'tone'> = {}) =>
      show(message, { ...options, tone: 'error' }),
    [show],
  );

  const dismiss = useCallback(() => setLeaving(true), []);
  // Stable, so a consumer can list it in an effect's dependencies without re-running it per toast.
  const api = useMemo(() => ({ show, error, dismiss }), [show, error, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-5 z-[80] flex justify-center px-4"
      >
        {toast ? (
          <ToastView
            key={toast.id}
            toast={toast}
            leaving={leaving}
            onLeave={() => setLeaving(true)}
            onGone={() => setToast((current) => (current?.id === toast.id ? null : current))}
          />
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({
  toast,
  leaving,
  onLeave,
  onGone,
}: {
  toast: ToastItem;
  leaving: boolean;
  onLeave: () => void;
  onGone: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (hovered || leaving) return;
    const timer = setTimeout(onLeave, toast.duration);
    return () => clearTimeout(timer);
  }, [hovered, leaving, toast.duration, onLeave]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hovering only pauses the timer, nothing depends on it
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onAnimationEnd={() => {
        if (leaving) onGone();
      }}
      className={cn(
        'pointer-events-auto flex max-w-[min(560px,100%)] items-center gap-3 rounded-lg px-3.5 py-2.5 text-crow-compact shadow-crow-2',
        toast.tone === 'error'
          ? 'bg-crow-danger text-crow-on-danger'
          : 'bg-crow-text text-crow-surface',
        leaving ? 'animate-crow-toast-out' : 'animate-crow-toast-in',
      )}
    >
      <span className="min-w-0 flex-1">{toast.message}</span>
      {toast.action ? (
        <button
          type="button"
          className="shrink-0 font-semibold underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
          onClick={() => {
            toast.action?.onClick();
            onLeave();
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
    </div>
  );
}
